import { useSyncExternalStore } from 'react';

/**
 * Temporary "Add to List" session list for finds (products).
 *
 * The admin clicks "Add to List" on the Products page to collect product IDs and
 * later pastes the compiled string into the Bulk Edit page. The list lives in
 * sessionStorage so it survives navigation/reloads within the same browser tab
 * but is discarded once the tab is closed.
 */

const STORAGE_KEY = 'finds_bulk_list_v1';

/** Default delimiter used when compiling the IDs into a single string. */
export const DEFAULT_DELIMITER = ', ';

type Listener = () => void;

const listeners = new Set<Listener>();

const isBrowser = () => typeof window !== 'undefined';

const load = (): number[] => {
  if (!isBrowser()) return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((value) => Number(value))
      .filter((value) => Number.isSafeInteger(value) && value > 0);
  } catch {
    return [];
  }
};

let ids: number[] = load();

const persist = () => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable (private mode / quota) — the in-memory list still works.
  }
};

const emit = () => {
  persist();
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Current list of collected IDs (ordered, no duplicates). */
export const getFindIds = (): number[] => ids;

/** React hook — subscribes a component to the session list. */
export const useFindIds = (): number[] => useSyncExternalStore(subscribe, getFindIds, getFindIds);

export const isInFindList = (id: number): boolean => ids.includes(id);

/** Adds an ID. Returns false when it was already in the list. */
export const addFindId = (id: number): boolean => {
  if (!Number.isSafeInteger(id) || id <= 0 || ids.includes(id)) return false;
  ids = [...ids, id];
  emit();
  return true;
};

export const removeFindId = (id: number): boolean => {
  if (!ids.includes(id)) return false;
  ids = ids.filter((value) => value !== id);
  emit();
  return true;
};

/** Adds the ID if missing, removes it if present. Returns true when now in list. */
export const toggleFindId = (id: number): boolean => {
  if (ids.includes(id)) {
    removeFindId(id);
    return false;
  }
  addFindId(id);
  return true;
};

/** Merges the given IDs into the list (used by "Load list" helpers). */
export const mergeFindIds = (incoming: number[]): number => {
  const next = new Set(ids);
  const before = next.size;
  incoming.forEach((id) => {
    if (Number.isSafeInteger(id) && id > 0) next.add(id);
  });
  if (next.size === before) return 0;
  ids = Array.from(next);
  emit();
  return next.size - before;
};

export const clearFindIds = () => {
  if (ids.length === 0) return;
  ids = [];
  emit();
};

/** Removes the given IDs from the list. */
export const removeFindIds = (toRemove: number[]) => {
  const removal = new Set(toRemove);
  const next = ids.filter((id) => !removal.has(id));
  if (next.length === ids.length) return;
  ids = next;
  emit();
};

/** Compiles the collected IDs into one delimiter-separated string. */
export const formatFindIds = (
  list: number[] = ids,
  delimiter: string = DEFAULT_DELIMITER,
): string => list.join(delimiter);

/**
 * Extracts IDs from free-form admin input.
 * Accepts IDs separated by commas, spaces, hyphens, newlines, pipes, or any mix
 * of them (e.g. "123, 32, 42" / "123 32 42" / "123 - 32 - 42").
 * Duplicates are removed and order is preserved.
 */
export const parseFindIds = (input: string): number[] => {
  if (!input) return [];
  const matches = input.match(/\d+/g) ?? [];
  const seen = new Set<number>();
  const result: number[] = [];
  for (const match of matches) {
    const value = Number(match);
    if (!Number.isSafeInteger(value) || value <= 0) continue;
    if (seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
};
